import { useState, useRef, useEffect, useMemo } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useCreditStore } from '@/stores/creditStore';
import { useUiStore, type TopbarOrg } from '@/stores/uiStore';
import { usePersonaStore } from '@/stores/personaStore';
import {
  useDashboardScopeStore,
  normalizeStatePostal,
} from '@/stores/dashboardScopeStore';
import { getPersonaList, personas } from '@/data/personas';
import { updateSession } from '@/api/session';
import { useNavigate } from 'react-router-dom';
import { ThemeSwitch } from './ThemeSwitch';
import styles from './Topbar.module.css';

const ORGS: TopbarOrg[] = ['Federal', 'State', 'Local', 'Private'];
/**
 * Phase A D4 — modes trimmed to what Phase A actually serves.
 * Roads + Transit are the only ground-truth modes the ZILM landing
 * endpoint accepts. The umbrella "All Available" entry keeps the
 * existing wiring (`updateSession({ mode: 'all' })`) intact.
 *
 * Previously: rail / airports / ports / multimodal. Those are dropped
 * from the source-of-truth here; any persisted user value pointing at
 * them falls back to 'roads' on Topbar mount (see effect below).
 */
const MODES: Array<{ value: string; label: string }> = [
  { value: 'all', label: 'All Available' },
  { value: 'roads', label: 'Roads' },
  { value: 'transit', label: 'Transit' },
];

const VALID_MODE_VALUES = new Set(MODES.map((m) => m.value));

/**
 * Phase C D7 — dashboard SCOPE dropdown.
 *
 * Distinct from `topbarOrg` (the user's organization-class chip). Scope is
 * an ephemeral view filter that lets a user temporarily focus the dashboard
 * tile data + map on a particular state without changing their org or
 * persona. Default: derived from `user.stateTerritory` on first mount;
 * null → 'Federal'. Selection writes to `dashboardScopeStore`, which the
 * DashboardPanel + DashboardStateMap subscribe to for tile re-fetching and
 * map zoom.
 */
const STATE_SCOPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'federal', label: 'National — All States' },
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' }, { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' }, { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' }, { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' }, { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' }, { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' }, { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' }, { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' }, { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' }, { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' }, { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' }, { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' }, { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' }, { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

interface TopbarProps {
  panelId?: string;
}

export function Topbar({ panelId: _panelId }: TopbarProps) {
  const user = useAuthStore((s) => s.user);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const remaining = useCreditStore((s) => s.remaining);
  const navigate = useNavigate();

  const topbarOrg = useUiStore((s) => s.topbarOrg);
  const topbarMode = useUiStore((s) => s.topbarMode);
  const setTopbarOrg = useUiStore((s) => s.setTopbarOrg);
  const setTopbarMode = useUiStore((s) => s.setTopbarMode);
  const openCustomize = useUiStore((s) => s.openCustomizeModal);

  const currentPersonaId = usePersonaStore((s) => s.currentPersonaId);
  const setCurrentPersona = usePersonaStore((s) => s.setCurrentPersona);
  const persona = personas[currentPersonaId] ?? personas['dot-executive'];

  // Phase C D7 — dashboard scope dropdown.
  const dashboardScopeKind = useDashboardScopeStore((s) => s.scope);
  const dashboardState = useDashboardScopeStore((s) => s.state);
  const setScope = useDashboardScopeStore((s) => s.setScope);
  const setScopeFromUser = useDashboardScopeStore((s) => s.setFromUser);

  const scopeSelectValue = useMemo(() => {
    if (dashboardScopeKind === 'state' && dashboardState) return dashboardState;
    return 'federal';
  }, [dashboardScopeKind, dashboardState]);

  // Initialise scope from the auth user on first mount (idempotent — see
  // dashboardScopeStore.setFromUser).
  useEffect(() => {
    if (!user) return;
    setScopeFromUser({
      stateTerritory: user.stateTerritory ?? null,
      persona: currentPersonaId,
    });
  }, [user, currentPersonaId, setScopeFromUser]);

  const [menuOpen, setMenuOpen] = useState(false);
  const [personaMenuOpen, setPersonaMenuOpen] = useState(false);
  const [zilmQuery, setZilmQuery] = useState('');
  const personaRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!personaRef.current) return;
      if (!personaRef.current.contains(e.target as Node)) setPersonaMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // Phase A D4 — if a previously-persisted topbarMode no longer exists in
  // the trimmed MODES list (e.g., 'rail', 'airports'), fall back to 'roads'.
  useEffect(() => {
    if (!VALID_MODE_VALUES.has(topbarMode)) {
      setTopbarMode('roads');
      void updateSession({ mode: 'roads' }).catch(() => {});
    }
  }, [topbarMode, setTopbarMode]);

  const handleOrgChange = (org: TopbarOrg) => {
    setTopbarOrg(org);
    void updateSession({ org }).catch(() => {});
  };
  const handleModeChange = (mode: string) => {
    setTopbarMode(mode);
    void updateSession({ mode }).catch(() => {});
    // When the user changes Mode while on the dashboard, push it onto
    // the URL as `?mode=<value>` so the panel re-renders with the new
    // filter (and so a deep-link survives reloads). Other panels read
    // `topbarMode` from the store directly and don't need URL sync.
    if (window.location.pathname.startsWith('/dashboard')) {
      const sp = new URLSearchParams(window.location.search);
      if (mode === 'all') sp.delete('mode');
      else sp.set('mode', mode);
      const qs = sp.toString();
      navigate(`${window.location.pathname}${qs ? `?${qs}` : ''}`);
    }
  };
  const handlePersonaChange = (personaId: string) => {
    setCurrentPersona(personaId);
    setPersonaMenuOpen(false);
    void updateSession({ personaId }).catch(() => {});
  };

  const handleScopeChange = (raw: string) => {
    // Update the legacy store (Topbar + DashboardStateMap consumers)
    // AND push the URL so the new DashboardCommandPanel picks up the
    // change via its URL→store sync effect. Without the URL push the
    // dropdown was effectively a no-op on the reimagined dashboard
    // because the new command store wasn't being updated from here.
    if (raw === 'federal') {
      setScope({ scope: 'federal' });
      navigate(`/dashboard/scope/federal${window.location.search}`);
      return;
    }
    const code = normalizeStatePostal(raw);
    if (code) {
      setScope({ scope: 'state', state: code });
      navigate(`/dashboard/scope/state/${code}${window.location.search}`);
    }
  };

  const submitZilm = () => {
    const q = zilmQuery.trim();
    if (!q) return;
    navigate(`/zilm?q=${encodeURIComponent(q)}`);
    setZilmQuery('');
  };

  const handleLogout = () => {
    clearAuth();
    navigate('/', { replace: true });
  };

  return (
    <header className={styles.topbar}>
      <label className={styles.srOnly} htmlFor="topbar-org">
        Organization scope
      </label>
      <select
        id="topbar-org"
        className={styles.selector}
        value={topbarOrg}
        onChange={(e) => handleOrgChange(e.target.value as TopbarOrg)}
        aria-label="Organization scope"
      >
        {ORGS.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>

      <label className={styles.srOnly} htmlFor="topbar-mode">
        Transportation mode
      </label>
      <select
        id="topbar-mode"
        className={styles.selector}
        value={topbarMode}
        onChange={(e) => handleModeChange(e.target.value)}
        aria-label="Transportation mode"
      >
        {MODES.map((m) => (
          <option key={m.value} value={m.value}>
            {m.label}
          </option>
        ))}
      </select>

      {/* Phase C D7 — dashboard SCOPE (state filter). */}
      <label className={styles.srOnly} htmlFor="topbar-scope">
        Dashboard scope
      </label>
      <select
        id="topbar-scope"
        className={styles.selector}
        value={scopeSelectValue}
        onChange={(e) => handleScopeChange(e.target.value)}
        aria-label="Dashboard scope (National or specific state)"
        data-testid="topbar-scope-select"
      >
        {STATE_SCOPE_OPTIONS.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label}
          </option>
        ))}
      </select>

      <div className={styles.zilmWrap}>
        <span className={styles.zilmIcon} aria-hidden="true">
          🔍
        </span>
        <input
          type="text"
          className={styles.zilmInput}
          placeholder="Ask ZILM..."
          aria-label="Ask ZILM"
          value={zilmQuery}
          onChange={(e) => setZilmQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submitZilm();
          }}
        />
      </div>

      <div className={styles.spacer} />

      <ThemeSwitch />

      <button
        type="button"
        className={styles.whatsNewBtn}
        onClick={openCustomize}
        title="Customize dashboard"
      >
        ✦ Customize
      </button>

      <div className={styles.personaWrap} ref={personaRef}>
        <button
          type="button"
          className={styles.personaChip}
          style={{
            background: `color-mix(in srgb, ${persona.color} 12%, transparent)`,
            color: persona.color,
            borderColor: `color-mix(in srgb, ${persona.color} 30%, transparent)`,
          }}
          onClick={() => setPersonaMenuOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={personaMenuOpen}
          aria-label={`Current persona: ${persona.name}. Click to change.`}
        >
          <span aria-hidden="true">{persona.emoji}</span>
          <span>{persona.name}</span>
        </button>
        {personaMenuOpen && (
          <ul className={styles.personaMenu} role="listbox" aria-label="Select persona">
            {getPersonaList().map((p) => (
              <li key={p.id} role="option" aria-selected={p.id === currentPersonaId}>
                <button
                  type="button"
                  className={styles.personaMenuItem}
                  onClick={() => handlePersonaChange(p.id)}
                  style={
                    p.id === currentPersonaId
                      ? { background: `color-mix(in srgb, ${p.color} 10%, transparent)` }
                      : undefined
                  }
                >
                  <span aria-hidden="true">{p.emoji}</span>
                  <span style={{ color: p.color, fontWeight: 600 }}>{p.name}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className={styles.credits} aria-label={`${remaining} credits remaining`}>
        <span className={styles.creditValue}>{remaining}</span> credits
      </div>

      <div className={styles.avatarWrap}>
        <button
          className={styles.avatar}
          aria-label="User menu"
          onClick={() => setMenuOpen(!menuOpen)}
          title={user?.name ?? 'User'}
        >
          {user?.name?.charAt(0).toUpperCase() ?? 'U'}
        </button>
        {menuOpen && (
          <div className={styles.userMenu} role="menu">
            <div className={styles.userMenuHeader}>
              <strong>{user?.name ?? 'User'}</strong>
              <span className={styles.userMenuEmail}>{user?.email ?? ''}</span>
            </div>
            <div className={styles.userMenuDivider} />
            <button className={styles.userMenuItem} role="menuitem" onClick={() => { setMenuOpen(false); navigate('/settings'); }}>
              My Account
            </button>
            <button className={styles.userMenuItem} role="menuitem" onClick={() => { setMenuOpen(false); navigate('/admin'); }}>
              Admin &amp; Credits
            </button>
            <div className={styles.userMenuDivider} />
            <button className={`${styles.userMenuItem} ${styles.userMenuLogout}`} role="menuitem" onClick={handleLogout}>
              Sign Out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
